import type { AllergiesState } from "./AllergiesContext";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";

export function transformAllergiesStateToRequest(
  state: AllergiesState,
): RequestBody<paths["/{tenantId}/patients/{patientId}"]["patch"]> {
  const allergiesPayload: any[] = [];

  // Create set of IDs to delete for quick lookup (prevents double-sending)
  const idsToDeleteSet = new Set(state.allergyIdsToDelete || []);

  // Add deletions from allergyIdsToDelete
  state.allergyIdsToDelete.forEach((id) => {
    if (id) {
      allergiesPayload.push({ id, op: "delete" });
    }
  });

  // If user answered "No" to having allergies, delete all existing allergies
  // (those with IDs that haven't already been marked for deletion)
  if (state.hasAllergies === false) {
    state.allergies.forEach((a) => {
      if (a.id && !idsToDeleteSet.has(a.id)) {
        allergiesPayload.push({ id: a.id, op: "delete" });
      }
    });
    // Return early with delete operations only and hasAllergies flag
    return {
      medicalHistory: {
        hasAllergies: false,
        ...(allergiesPayload.length > 0 ? { allergies: allergiesPayload } : {}),
      },
    };
  }

  // Add allergies to keep (excluding those marked for deletion)
  state.allergies
    .filter((a) => !a.id || !idsToDeleteSet.has(a.id))
    .forEach((a) => {
      const entry: any = {};
      if (a.id) entry.id = a.id;
      if (a.allergy) entry.allergy = a.allergy;
      if (a.name) entry.name = a.name;
      if (a.other) entry.other = a.other;
      if (a.comments) entry.comments = a.comments;
      if (a.isUnderTreatment !== null) entry.isUnderTreatment = a.isUnderTreatment;
      if (a.treatmentDescription) entry.treatmentDescription = a.treatmentDescription;
      allergiesPayload.push(entry);
    });

  const medicalHistoryPayload: any = {
    hasAllergies: state.hasAllergies ?? null,
  };

  if (allergiesPayload.length > 0) {
    medicalHistoryPayload.allergies = allergiesPayload;
  }

  return {
    medicalHistory: medicalHistoryPayload,
  };
}
